import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { LegalSection, LegalList } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle="How Vawick Consulting UG processes and protects your personal data."
      lastUpdated="June 2026"
    >
      <LegalSection title="1. Controller">
        <p>
          The controller responsible for data processing on this website and within the nexus-vs
          platform is:
        </p>
        <p>
          Vawick Consulting UG (haftungsbeschränkt)<br />
          [Street and House Number], [Postal Code] [City], Germany<br />
          Email:{" "}
          <a
            href="mailto:support@vawick.de"
            className="text-white hover:text-[#FF4F00] transition-colors"
          >
            support@vawick.de
          </a>
        </p>
      </LegalSection>

      <LegalSection title="2. Data We Collect">
        <p><strong className="text-white">Contact and Account Data</strong></p>
        <LegalList items={[
          "Name, company name, and job title",
          "Email address and phone number",
          "Login credentials (email + hashed password — plaintext passwords are never stored)",
          "Billing details (processed via payment provider — card data never touches our servers)",
        ]} />
        <p><strong className="text-white">Usage and Technical Data</strong></p>
        <LegalList items={[
          "IP address, browser type, operating system, and referral source",
          "Pages visited, features used, and session duration",
          "Error logs and performance metrics",
        ]} />
        <p><strong className="text-white">Data Entered by Your Organisation</strong></p>
        <p>
          As a SaaS platform for contractor management, nexus-vs processes data your organisation
          enters on behalf of your contractors (names, contract documents, time records, payout data).
          You are the data controller for this data; we act as your data processor under a separate{" "}
          <Link to="/dpa" className="text-white hover:text-[#FF4F00] transition-colors">
            Data Processing Agreement
          </Link>.
        </p>
      </LegalSection>

      <LegalSection title="3. Legal Basis for Processing">
        <LegalList items={[
          "Art. 6(1)(b) GDPR — processing is necessary to perform the contract (providing nexus-vs)",
          "Art. 6(1)(c) GDPR — processing is necessary to comply with a legal obligation (e.g. tax records)",
          "Art. 6(1)(f) GDPR — processing is necessary for our legitimate interests (security, fraud prevention, product improvement)",
          "Art. 6(1)(a) GDPR — your consent, where specifically requested (e.g. marketing emails)",
        ]} />
      </LegalSection>

      <LegalSection title="4. How We Use Your Data">
        <LegalList items={[
          "To create and manage your account and deliver the nexus-vs service",
          "To process payments and issue invoices",
          "To send transactional emails (confirmations, password resets, usage notifications)",
          "To provide technical support and respond to enquiries",
          "To detect and prevent fraud, abuse, and security incidents",
          "To improve the platform through aggregated, anonymised usage analytics",
          "To fulfil legal and regulatory obligations",
        ]} />
      </LegalSection>

      <LegalSection title="5. Data Retention">
        <p>
          We retain personal data only as long as necessary for the purposes described here, or
          as required by applicable law.
        </p>
        <LegalList items={[
          "Account data — retained for the duration of the contract; deleted within 30 days of termination upon request",
          "Invoice and payment records — 10 years pursuant to §§ 238, 257 HGB (German Commercial Code)",
          "Support communications — 3 years",
          "Server access logs — 30 days",
        ]} />
      </LegalSection>

      <LegalSection title="6. Third-Party Service Providers">
        <p>
          We use the following sub-processors to deliver our service. Each is bound by a data
          processing agreement and GDPR-compliant safeguards:
        </p>
        <LegalList items={[
          "Supabase, Inc. — database and authentication; data stored in EU (Frankfurt) region",
          "Hosting provider — EU-based server infrastructure",
          "Payment processor — PCI-DSS compliant; card data is not shared with or stored by us",
        ]} />
        <p>We do not sell, rent, or share your personal data with third parties for marketing purposes.</p>
      </LegalSection>

      <LegalSection title="7. International Data Transfers">
        <p>
          We store and process all data within the European Union. If any sub-processor transfer
          to a third country becomes necessary, we ensure adequate safeguards (Standard Contractual
          Clauses per Art. 46 GDPR, or an adequacy decision) are in place.
        </p>
      </LegalSection>

      <LegalSection title="8. Your Rights Under GDPR">
        <p>You have the following rights regarding your personal data:</p>
        <LegalList items={[
          "Right of access (Art. 15 GDPR) — request a copy of the data we hold about you",
          "Right to rectification (Art. 16 GDPR) — correct inaccurate or incomplete data",
          "Right to erasure (Art. 17 GDPR) — request deletion of your data ('right to be forgotten')",
          "Right to restriction of processing (Art. 18 GDPR)",
          "Right to data portability (Art. 20 GDPR) — receive your data in a machine-readable format",
          "Right to object (Art. 21 GDPR) — object to processing based on legitimate interests",
          "Right to withdraw consent at any time, without affecting the lawfulness of prior processing",
        ]} />
        <p>
          To exercise any of these rights, contact us at{" "}
          <a
            href="mailto:support@vawick.de"
            className="text-white hover:text-[#FF4F00] transition-colors"
          >
            support@vawick.de
          </a>. We will respond within 30 days.
        </p>
      </LegalSection>

      <LegalSection title="9. Right to Lodge a Complaint">
        <p>
          You have the right to lodge a complaint with a supervisory authority if you believe your
          data is being processed unlawfully. The competent authority for Vawick Consulting UG is:
        </p>
        <p>
          [Name of competent Data Protection Authority]<br />
          [Address]
        </p>
        <p>
          You may also contact the German Federal Commissioner for Data Protection and Freedom of
          Information (BfDI) or the supervisory authority of your EU member state.
        </p>
      </LegalSection>

      <LegalSection title="10. Cookies">
        <p>
          nexus-vs uses only strictly necessary cookies required for authentication and session
          management. We do not use third-party tracking cookies or advertising cookies. No consent
          banner is required for strictly necessary cookies under the ePrivacy Directive.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy periodically. Material changes will be communicated
          via email or a notice within the platform at least 30 days before taking effect. The date
          at the top of this page reflects the most recent revision.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
